import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Archive, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { useProduct, useArchiveProduct, useDeleteProduct, useUpdateProduct } from '@/hooks/useProducts';
import { Product } from '@/services/products.service';
import { ProductEditProvider, useProductEdit } from '@/contexts/ProductEditContext';
import { FloatingSaveButton } from '@/components/ui/floating-save-button';
import ProductOverviewTab from './tabs/ProductOverviewTab';
import ProductEndpointsTab from './tabs/ProductEndpointsTab';
import ProductAutomationNodesTab from './tabs/ProductAutomationNodesTab';
import ProductBillingTab from './tabs/ProductBillingTab';
import ProductFeatureFlagsTab from './tabs/ProductFeatureFlagsTab';
import { useTranslation } from 'react-i18next';


function getStatusBadgeVariant(status: string | undefined): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case 'published':
      return 'default';
    case 'draft':
      return 'secondary';
    case 'archived':
      return 'outline';
    case 'suspended':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function ProductDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch product data
  const { 
    data: product, 
    isLoading, 
    error,
    refetch 
  } = useProduct(id!);

  // Mutations
  const archiveProductMutation = useArchiveProduct();
  const deleteProductMutation = useDeleteProduct();
  const updateProductMutation = useUpdateProduct();

  const { 
    hasChanges, 
    getUpdatedProduct, 
    isSaving, 
    setSaving, 
    initializeProduct 
  } = useProductEdit();

  // Initialize the context with product data when it loads
  useEffect(() => {
    if (product) {
      initializeProduct(product);
    }
  }, [product, initializeProduct]);

  const handleSave = async () => {
    if (!product || !hasChanges) return;
    
    setSaving(true);
    try {
      const changes = getUpdatedProduct();
      console.log('Saving product changes:', changes);
      
      const updatedProduct = await updateProductMutation.mutateAsync({
        id: product.id,
        data: changes
      });
      
      // Update the context with the new product data from server
      initializeProduct(updatedProduct);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleProductChange = (updates: Partial<Product>) => {
    // This function will be used by tab components to update product data
    console.log('Product changes:', updates);
    // In a real implementation, this would update local state or trigger mutations
  };

  const handleCancel = () => {
    // Reload the page to clear all local state
    window.location.reload();
  };

  const handleArchive = async () => {
    if (!product) return;
    
    try {
      await archiveProductMutation.mutateAsync(product.id);
      // Product will be updated automatically by React Query
    } catch (error) {
      console.error('Failed to archive product:', error);
    }
  };

  const handleDelete = async () => {
    if (!product) return;
    
    const confirmed = window.confirm('Are you sure you want to delete this product? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteProductMutation.mutateAsync(product.id);
      navigate('/provider/products');
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const handleBack = () => {
    navigate('/provider/products');
  };

  if (isLoading) {
    return (
      <ProviderLayout>
        <div className="container mx-auto p-6 max-w-7xl">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <Skeleton className="h-4 w-24" />
          </Button>
          
          <div className="mb-6">
            <div className="flex items-start justify-between">
              <div>
                <Skeleton className="h-8 w-64 mb-2" />
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-12" />
                </div>
                <Skeleton className="h-4 w-96" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-16" />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (error) {
    return (
      <ProviderLayout>
        <div className="container mx-auto p-6 max-w-7xl">
          <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('products.backToProducts')}
          </Button>
          
          <Alert variant="destructive">
            <AlertDescription>
              {t('errors.failedToLoadProduct', 'Failed to load product.')}{' '}
              {error.message}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetch()}
                className="ml-4"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('common.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </ProviderLayout>
    );
  }

  if (!product) {
    return (
      <ProviderLayout>
        <div className="container mx-auto p-6 max-w-7xl">
          <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('products.backToProducts')}
          </Button>
          
          <Alert>
            <AlertDescription>
              {t('products.errors.notFound', 'Product not found.')}
            </AlertDescription>
          </Alert>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('products.backToProducts')}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{product?.name || t('products.loadingName', 'Loading...')}</h1>
                {product?.status && (
                  <Badge variant={getStatusBadgeVariant(product.status)}>
                {product.status_label ||
                  t(`products.statuses.${product.status}`, {
                    defaultValue: product.status.charAt(0).toUpperCase() + product.status.slice(1),
                  })}
              </Badge>
            )}
              </div>
              <p className="text-muted-foreground">
                {product?.description || t('products.loadingDescription', 'Loading description...')}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => refetch()}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                {t('common.refresh')}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleArchive}
                disabled={archiveProductMutation.isPending}
              >
                {archiveProductMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4 mr-2" />
                )}
                {t('products.archive')}
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleteProductMutation.isPending}
              >
                {deleteProductMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {t('products.delete')}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">{t('products.overview')}</TabsTrigger>
            <TabsTrigger value="endpoints">{t('products.endpoints')}</TabsTrigger>
            <TabsTrigger value="automation">{t('products.automationNodes')}</TabsTrigger>
            <TabsTrigger value="billing">{t('products.billing')}</TabsTrigger>
            <TabsTrigger value="feature-flags">
              {t('products.featureFlags')}
              <span className="ml-1 text-xs text-muted-foreground">({t('products.optional')})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <ProductOverviewTab 
              product={product} 
              onProductChange={handleProductChange}
            />
          </TabsContent>

          <TabsContent value="endpoints">
            <ProductEndpointsTab productId={product.id} />
          </TabsContent>

          <TabsContent value="automation">
            <ProductAutomationNodesTab productId={product.id} />
          </TabsContent>

          <TabsContent value="billing">
            <ProductBillingTab productId={product.id} supportedLanguages={product.supported_languages || ['en']} />
          </TabsContent>

          <TabsContent value="feature-flags">
            <ProductFeatureFlagsTab productId={product.id} />
          </TabsContent>
        </Tabs>

        {/* Floating Save Button */}
        <FloatingSaveButton
          isVisible={hasChanges}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      </div>
    </ProviderLayout>
  );
}

export default function ProductDetail() {
  return (
    <ProductEditProvider>
      <ProductDetailContent />
    </ProductEditProvider>
  );
}
